/**
 * API drafts — wrapping autour de `apiClient` (axios).
 *
 * Toutes les fonctions retournent des promesses typées sur `types/drafts.ts`
 * (auto-générés depuis l'OpenAPI). Aucune transformation BE↔FE — le BE est
 * source de vérité, on s'aligne via codegen.
 */

import { apiClient } from './client'
import type { components } from '@/types/api-generated'
import type {
  ApproveDraftRequest,
  DraftKind,
  DraftListResponse,
  DraftListSort,
  DraftPendingCountResponse,
  DraftRephraseRequest,
  DraftRephraseResponse,
  DraftResponse,
  DraftStatsResponse,
  DraftStatus,
  GenerateDraftsRequest,
  Platform,
  RegenerateDraftRequest,
} from '@/types/drafts'

// PR2 (2026-05-10) — réponse `POST /drafts/{id}/approve` enrichie avec
// `rendered_mockup_ids` + `previews` (presigned URLs pour la modale FE).
export type DraftApproveResponse = components['schemas']['DraftApproveResponse']
export type DraftMockupPreview = components['schemas']['DraftMockupPreview']

// Refactor 2026-05-15 — `POST /works/{id}/drafts/generate` retourne
// désormais un batch enqueueed (réponse immédiate, plus de 504 nginx).
// Le FE poll `GET /generation-jobs/batch/{id}` jusqu'à terminé.
export type EnqueueBatchResponse = components['schemas']['EnqueueBatchResponse']
export type BatchStatusResponse = components['schemas']['BatchStatusResponse']

export interface DraftListFilters {
  status?: DraftStatus
  platform?: Platform
  kind?: DraftKind
  q?: string
  sort?: DraftListSort
  limit?: number
  offset?: number
}

export const draftsApi = {
  listForWork: async (workId: number): Promise<DraftResponse[]> => {
    const { data } = await apiClient.get<DraftResponse[]>(`/works/${workId}/drafts`)
    return data
  },

  // ── Cross-œuvres — page /drafts (S3 câblage) ──────────────────────────
  listAll: async (filters: DraftListFilters = {}): Promise<DraftListResponse> => {
    const params: Record<string, string | number> = {}
    if (filters.status) params.status = filters.status
    if (filters.platform) params.platform = filters.platform
    if (filters.kind) params.kind = filters.kind
    if (filters.q) params.q = filters.q
    if (filters.sort) params.sort = filters.sort
    if (filters.limit !== undefined) params.limit = filters.limit
    if (filters.offset !== undefined) params.offset = filters.offset
    const { data } = await apiClient.get<DraftListResponse>('/drafts', { params })
    return data
  },

  pendingCount: async (): Promise<DraftPendingCountResponse> => {
    const { data } = await apiClient.get<DraftPendingCountResponse>('/drafts/pending-count')
    return data
  },

  stats: async (): Promise<DraftStatsResponse> => {
    const { data } = await apiClient.get<DraftStatsResponse>('/drafts/stats')
    return data
  },

  generate: async (
    workId: number,
    payload: GenerateDraftsRequest = { kinds: null, force: false },
  ): Promise<EnqueueBatchResponse> => {
    // Refactor 2026-05-15 — réponse immédiate (202 Accepted). Le worker
    // scheduler exécute la génération en arrière-plan. Pas de timeout
    // étendu nécessaire ; le payload `kinds`/`force` est conservé pour
    // compat mais ignoré côté BE (mode missing-only).
    const { data } = await apiClient.post<EnqueueBatchResponse>(
      `/works/${workId}/drafts/generate`,
      payload,
    )
    return data
  },

  // Polling du batch retourné par `generate` — appelé par
  // `useGenerationBatchPolling` toutes les 3s jusqu'à running+scheduled=0.
  getBatchStatus: async (batchId: string): Promise<BatchStatusResponse> => {
    const { data } = await apiClient.get<BatchStatusResponse>(`/generation-jobs/batch/${batchId}`)
    return data
  },

  approve: async (draftId: number, payload: ApproveDraftRequest): Promise<DraftApproveResponse> => {
    const { data } = await apiClient.post<DraftApproveResponse>(
      `/drafts/${draftId}/approve`,
      payload,
      // Render synchrone × N composites Pillow ~1-2s × N. Borne 8 ⇒
      // 16s max. Timeout 30s pour absorber les pics MinIO.
      { timeout: 30_000 },
    )
    return data
  },

  regenerate: async (
    draftId: number,
    payload: RegenerateDraftRequest = { extra_hint: null },
  ): Promise<DraftResponse> => {
    const { data } = await apiClient.post<DraftResponse>(`/drafts/${draftId}/regenerate`, payload, {
      timeout: 60_000,
    })
    return data
  },

  reject: async (draftId: number): Promise<DraftResponse> => {
    const { data } = await apiClient.post<DraftResponse>(`/drafts/${draftId}/reject`)
    return data
  },

  reopen: async (draftId: number): Promise<DraftResponse> => {
    const { data } = await apiClient.post<DraftResponse>(`/drafts/${draftId}/reopen`)
    return data
  },

  rephrase: async (payload: DraftRephraseRequest): Promise<DraftRephraseResponse> => {
    const { data } = await apiClient.post<DraftRephraseResponse>('/works/draft-rephrase', payload, {
      timeout: 30_000,
    })
    return data
  },
}
